import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  QA_NAME_SHOW_LESS_BUTTON,
  QA_NAME_SHOW_MORE_BUTTON,
} from '../../../constants/qa'
import { useReadMore, useThemeMode } from '../../../hooks'
import { linkifyHtml, openHtmlLinksInNewTab } from '../../../utils/html'
import { getThemeContentHtml } from '../../../utils/themeContent'
import Button, { BUTTON_HEIGHT_24, BUTTON_HEIGHT_32 } from '../../Button'
import { EntityProfileHeaderInfoItemLoading } from '../../Entity'
import Text from '../../Text'
import { RequestTranslationToolbar } from '../RequestTranslationToolbar'

import {
  REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_ACTION_QA_NAMES,
  REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_ACTIONS_KEY_TRANSLATE,
  REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_CLAMPING_LINES_COUNT,
} from './RequestProfileHeaderContent.constants'
import { useRequestProfileHeaderDescriptionSection } from './RequestProfileHeaderContent.hooks'

import * as styles from './RequestProfileHeaderContent.module.css'

import EditIcon from '../../../icons/edit.svg'
import PlusIcon from '../../../icons/plus.svg'
import TranslateIcon from '../../../icons/translate.svg'

function RequestProfileHeaderContent({ isLoading }) {
  const { t } = useTranslation()

  const { theme } = useThemeMode()

  const {
    canEditDescription,
    descriptionHtml,
    hasDescriptionContent,
    isRequestTranslateOpen,
    requestTranslateProps,
    onClickEditDescription,
    onClickOpenRequestTranslate,
  } = useRequestProfileHeaderDescriptionSection()

  const {
    handleClickReadMore,
    isReadMore: isDescriptionExpanded,
    showReadMore: showDescriptionExpandButton,
    textRef: descriptionContentRef,
  } = useReadMore({
    maxLines: REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_CLAMPING_LINES_COUNT,
    textData: descriptionHtml,
  })

  const showTranslateButton = hasDescriptionContent && !isRequestTranslateOpen

  const showActions = canEditDescription || showTranslateButton

  const descriptionRowClassName = classNames(styles.descriptionRow, {
    [styles.descriptionRowWithActions]: showActions,
  })

  const sectionClassName = classNames(styles.section, {
    [styles.loading]: isLoading,
  })

  return (
    <div className={sectionClassName}>
      {isLoading ? (
        <EntityProfileHeaderInfoItemLoading />
      ) : (
        <>
          {isRequestTranslateOpen ? (
            <div className={styles.translate}>
              <RequestTranslationToolbar {...requestTranslateProps} />
            </div>
          ) : null}
          <Text isSecondary className={styles.sectionLabel}>
            {t('RequestProfile.HeaderDescriptionLabelKey')}
          </Text>
          <div className={styles.descriptionContent}>
            <div className={descriptionRowClassName}>
              <div className={styles.descriptionText}>
                {showActions ? (
                  <div className={styles.actions}>
                    {showTranslateButton ? (
                      <Button.Outlined
                        isGrey
                        beforeIconProps={{ Icon: TranslateIcon }}
                        height={BUTTON_HEIGHT_32}
                        qaName={
                          REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_ACTION_QA_NAMES[
                            REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_ACTIONS_KEY_TRANSLATE
                          ]
                        }
                        tooltip={t(
                          'RequestProfile.TranslateDescriptionBtnTooltipKey'
                        )}
                        onClick={onClickOpenRequestTranslate}
                      />
                    ) : null}
                    {canEditDescription ? (
                      hasDescriptionContent ? (
                        <Button.Outlined
                          isGrey
                          beforeIconProps={{ Icon: EditIcon }}
                          height={BUTTON_HEIGHT_32}
                          qaName={
                            REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_ACTION_QA_NAMES.edit
                          }
                          tooltip={t('Profile.EditInfoItemBtnTooltipKey')}
                          onClick={onClickEditDescription}
                        />
                      ) : (
                        <Button.Outlined
                          isGrey
                          beforeIconProps={{ Icon: PlusIcon }}
                          height={BUTTON_HEIGHT_32}
                          qaName={
                            REQUEST_PROFILE_HEADER_CONTENT_DESCRIPTION_ACTION_QA_NAMES.add
                          }
                          tooltip={t(
                            'RequestProfile.AddDescriptionBtnTooltipKey'
                          )}
                          onClick={onClickEditDescription}
                        />
                      )
                    ) : null}
                  </div>
                ) : null}
                {hasDescriptionContent ? (
                  <div
                    className={styles.message}
                    dangerouslySetInnerHTML={{
                      __html: getThemeContentHtml(
                        openHtmlLinksInNewTab(linkifyHtml(descriptionHtml)),
                        theme
                      ),
                    }}
                    ref={descriptionContentRef}
                  />
                ) : (
                  <Text disabled isSecondary>
                    {t('RequestProfile.HeaderWithoutDescriptionLabelKey')}
                  </Text>
                )}
              </div>
            </div>
            {hasDescriptionContent && showDescriptionExpandButton ? (
              <Button.Contained
                isGrey
                className={styles.readMoreButton}
                height={BUTTON_HEIGHT_24}
                qaName={
                  isDescriptionExpanded
                    ? QA_NAME_SHOW_LESS_BUTTON
                    : QA_NAME_SHOW_MORE_BUTTON
                }
                text={t(
                  isDescriptionExpanded
                    ? 'Notification.CommonReadLessBtnKey'
                    : 'Notification.CommonReadMoreBtnKey'
                )}
                onClick={handleClickReadMore}
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

export default RequestProfileHeaderContent
